import { useMemo, useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Avatar, AvatarStack, type AvatarUser } from "@/components/ui/Avatar";
import { PersonPicker } from "@/components/board/PersonPicker";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface PersonCellProps {
  value: string[];
  workspaceId?: UUID | null;
  boardId?: UUID | null;
  allowMultiple?: boolean;
  rowH?: number;
  onChange?: (next: UUID[]) => void;
}

export function PersonCell({
  value,
  workspaceId,
  boardId,
  allowMultiple = true,
  rowH = 44,
  onChange,
}: PersonCellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // Resolve user_id → AvatarUser via the board's people directory, which
  // includes cross-department GUESTS (assignees who aren't workspace members);
  // the workspace-members list alone can't render them.
  const people = useBoardPeople(boardId ?? undefined);
  const byId = useMemo(() => {
    const map = new Map<string, AvatarUser>();
    for (const m of people.data ?? []) {
      const name = m.display_name ?? m.email;
      map.set(m.user_id, {
        id: m.user_id,
        display_name: name,
        initials: deriveInitials(name),
        avatar_color: deriveAvatarColor(m.user_id),
        username: m.username,
        department_workspace_id: m.department_workspace_id ?? null,
        department_color: m.department_color ?? null,
      });
    }
    return map;
  }, [people.data]);

  const users = (value || []).map((id) => byId.get(id)).filter((u): u is AvatarUser => Boolean(u));
  // For ids we don't know yet (e.g. just assigned, members refetch pending),
  // show a neutral placeholder so the count is still meaningful.
  const unknownCount = (value || []).length - users.length;
  const editable = Boolean(workspaceId && onChange);

  return (
    <>
      <CellWrap rowH={rowH} onClick={editable ? () => setOpen(true) : undefined}>
        <div ref={ref} style={{ width: "100%", display: "flex", alignItems: "center" }}>
          {users.length === 0 && unknownCount === 0 ? (
            <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>+ {t.assign}</span>
          ) : users.length === 1 && unknownCount === 0 ? (
            <>
              <Avatar user={users[0]} size={22} />
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 13,
                  color: "var(--ink-1)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {users[0]!.display_name.split(" ")[0]}
              </span>
            </>
          ) : (
            <AvatarStack users={users} size={22} max={3} />
          )}
        </div>
      </CellWrap>
      {open && editable ? (
        <PersonPicker
          anchor={ref.current}
          value={(value || []) as UUID[]}
          allowMultiple={allowMultiple}
          workspaceId={workspaceId as UUID}
          boardId={(boardId ?? undefined) as UUID | undefined}
          onClose={() => setOpen(false)}
          onChange={(next) => onChange?.(next)}
        />
      ) : null}
    </>
  );
}
